import { Tracker, User } from '@appliedsystems/product-intelligence';
import { useEffect, useRef } from 'react';
import { useHppDataStore } from '../components/HostedPaymentPageContainer/useHppData';
import { useAgencyDetailsStore } from '../store/AgencyDetail';
import { getConfig } from '../util/config';

export const useTrackerInitializer = () => {
  const tracker = useRef<Tracker | null>(null);
  const isTrackerReady = useRef(false);
  const { hppData } = useHppDataStore();
  const { data: agencyDetails } = useAgencyDetailsStore();
  const PRODUCT_SHORT_NAME = 'pay';
  const tenantIds = (getConfig('REACT_APP_PENDO_TRACKING_ENABLED') || '').split(' ');
  const isPendoTrackingEnabledForTenant =
    (agencyDetails?.tenantId && tenantIds.includes(agencyDetails.tenantId)) || tenantIds.includes('*');

  useEffect(() => {
    if (!agencyDetails) return;

    const agencyName = agencyDetails.name;
    const tenantId = agencyDetails.tenantId;
    const agencyBrandLocation = agencyDetails.brandLocation;
    const agencyPhoneNumber = agencyDetails.phoneNumber;
    const agencyEmail = agencyDetails.email;
    const merchantAccountId = agencyDetails.merchantAccountId;
    const merchantId = agencyDetails.merchantId;

    const firstName = hppData.firstName;
    const lastName = hppData.lastName;
    const userEmail = hppData.userEmail;
    const accountCode = hppData.accountCode;

    const agencyProps = {
      agencyName,
      tenantId,
      agencyBrandLocation,
      agencyPhoneNumber,
      agencyEmail,
      status: agencyDetails.status,
      appliedProductId: agencyDetails.appliedProductId,
      currency: agencyDetails.currency,
      hppUrlRedirect: agencyDetails.hppUrlRedirect,
      merchantAccountId,
      merchantId,
    };

    const userProps = {
      firstName,
      lastName,
      userEmail,
      postalCode: hppData.postalCode,
      phoneNumber: hppData.phoneNumber,
      accountCode,
      payBy: hppData.payBy,
      paymentWorkflow: hppData.paymentWorkflow,
      invoiceNumber: hppData.invoiceNumber,
      businessName: hppData.businessName,
      checkoutVariant: hppData.checkoutBEnabled ? 'B' : 'A',
      mobileWalletsEnabled: hppData.mobileWalletsEnabled,
      ipAddress: agencyDetails.ipAddress,
    };

    const user: User = {
      Email: userEmail,
      _email: userEmail,
      UserIdentifier: userEmail,
      Agency: {
        Id: tenantId,
        ClientId: hppData.clientId,
        Properties: {
          otherProperties: {
            pendo: agencyProps,
          },
          toJson: () => ({
            agencyProps,
          }),
          toPendoJson: () => ({
            [`${PRODUCT_SHORT_NAME}_tenantId`]: tenantId,
            [`${PRODUCT_SHORT_NAME}_agencyName`]: agencyName,
            [`${PRODUCT_SHORT_NAME}_agencyBrandLocation`]: agencyBrandLocation,
            [`${PRODUCT_SHORT_NAME}_agencyPhoneNumber`]: agencyPhoneNumber,
            [`${PRODUCT_SHORT_NAME}_agencyEmail`]: agencyEmail,
            [`${PRODUCT_SHORT_NAME}_merchantAccountId`]: merchantAccountId,
            [`${PRODUCT_SHORT_NAME}_merchantId`]: merchantId,
          }),
        },
        ToPendoAccount: () => ({
          [`${PRODUCT_SHORT_NAME}_tenantId`]: tenantId,
          [`${PRODUCT_SHORT_NAME}_pay_userEmail`]: userEmail,
        }),
      },
      URL: window.location.href,
      UserLanguage: navigator.language,
      CountryCode: getConfig('REACT_APP_LOCALE').toUpperCase(),
      Personas: [],
      Properties: {
        otherProperties: {
          pendo: userProps,
        },
        toJson: () => ({
          userProps,
        }),
        toPendoJson: () => ({
          [`${PRODUCT_SHORT_NAME}_userEmail`]: userEmail,
          [`${PRODUCT_SHORT_NAME}_userFirstName`]: firstName,
          [`${PRODUCT_SHORT_NAME}_userLastName`]: lastName,
          [`${PRODUCT_SHORT_NAME}_accountCode`]: accountCode,
        }),
      },
      ToPendoVisitor: () => ({
        id: userEmail,
        email: userEmail,
      }),
    };

    try {
      // Initialize tracker
      tracker.current = new Tracker({
        pendo: {
          enabled: isPendoTrackingEnabledForTenant,
          productShortName: PRODUCT_SHORT_NAME,
          apiKey: getConfig('REACT_APP_PENDO_API_KEY') || '',
          trackAnonymous: true,
          loadOnInit: true,
          documentElementId: 'pendo-bootstrapper',
          disableTrackEvent: false,
          disableTrackScreenView: false,
          disableTrackItemClicked: false,
        },
      });

      // Set user on the tracker
      tracker.current
        .setUser(user)
        .then(() => {
          isTrackerReady.current = true;
        })
        .catch((error) => {
          console.error('Error setting user on tracker:', error);
        });
    } catch (error) {
      console.error('Error initializing tracker:', error);
    }
  }, [agencyDetails, hppData, isPendoTrackingEnabledForTenant, PRODUCT_SHORT_NAME]);
};
